import React, { useEffect, useState, useCallback } from "react";
import AllowancePage from "../../entities/AllowancePage";
import LoaderHelper from "../../utils/Loader/LoaderHelper";
import AuthService from "../../api/AuthService";
import { useAlert } from "../../context/AlertContext";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";

const Allowance = () => {
  const location = useLocation();
  const { taxCountry } = location.state || {};
  const [allowance, setAllowance] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { showAlert } = useAlert();
  const [leaveData, setleaveData] = useState([]);
  const [allowanceOpen, setAllowanceOpen] = useState([])
  const [travelAllowances, setTravelAllowances] = useState([])
  const [taxPeriod, setTaxPeriod] = useState([]);
  const [selectedTaxYear, setSelectedTaxYear] = useState("");

  const getTaxPeriod = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getTaxPeriod(taxCountry);
      if (result?.success) {
        setTaxPeriod(result?.data || []);
        const currentYear = new Date().getFullYear();
        const matchedYear = result?.data?.find((item) => {
          const startYear = dayjs(item?.start).year();
          return startYear === currentYear;
        });
        setSelectedTaxYear(matchedYear ? matchedYear._id : result?.data?.[0]?._id || "");
        LoaderHelper.loaderStatus(false);
      } else {
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  useEffect(() => {
    getTaxPeriod();
  }, []);

  const getAllowance = useCallback(async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const selectedFY = taxPeriod?.find((p) => p._id === selectedTaxYear);
      const startDate = selectedFY ? dayjs(selectedFY.start).format("DD-MM-YYYY") : "";
      const endDate = selectedFY ? dayjs(selectedFY.end).format("DD-MM-YYYY") : "";

      const result = await AuthService.getAdmAllowance({
        country: taxCountry,
        start: startDate,
        end: endDate,
      });
      if (result?.success) {
        const allowancesList = result?.data?.allowance || [];
        setAllowance(allowancesList);
      } else {
        setAllowance([]);
        showAlert(result?.message || "Failed to fetch allowances", "error");
      }
    } catch (error) {
      setAllowance([]);
      showAlert("An error occurred while fetching allowances", "error");
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  }, [selectedTaxYear, taxPeriod, taxCountry, showAlert]);

  useEffect(() => {
    if (selectedTaxYear && taxPeriod.length > 0) {
      getAllowance();
      getTravelAllowance(); // Fetch travel allowances when FY changes
    }
  }, [selectedTaxYear, taxPeriod]);

  useEffect(() => {
    getLeaveType();
    // Don't call getTravelAllowance here - it will be called when FY is selected
  }, []);

  const handleCreateAllowance = async (leave, isPaid) => {
    LoaderHelper.loaderStatus(true);
    try {
      const selectedFY = taxPeriod?.find((p) => p._id === selectedTaxYear);
      const startDate = selectedFY ? dayjs(selectedFY.start).format("YYYY-MM-DD") : "";
      const endDate = selectedFY ? dayjs(selectedFY.end).format("YYYY-MM-DD") : "";
      const country = taxCountry && taxCountry.trim() !== "" ? taxCountry : null;

      const result = await AuthService.CreateAllowance(leave, isPaid, startDate, endDate, country);
      if (result?.success) {
        getAllowance();
        showAlert(result?.message, "success")
      } else {
        showAlert(result?.message);
      }
    } catch (error) {
      showAlert(error);
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleEditAllowance = async (allowanceText, allowanceTax, id) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.EditAllowance(allowanceText, allowanceTax, id);

      if (result?.success) {
        getAllowance();
        showAlert(result?.message, "success")
      } else {
        showAlert(result?.message);
      }
    } catch (error) {
      showAlert(error);
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };


  const handleDeleteAllowance = async (id) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.deleteAllowance(id);
      if (result?.success) {
        getAllowance();
        showAlert(result?.message, "success")
      } else {
        showAlert(result?.message);
      }
    } catch (error) {
      showAlert(error);
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleLeaveType = async (leave, isPaid) => {
    setLoading(true);
    try {
      const result = await AuthService.handleAccountLeaveType(leave, isPaid);
      if (result?.success) {
        showAlert(result?.message, "success");
        setOpen(false);
        getLeaveType(); // Refresh leave types
      } else {
        showAlert(result?.message || "Failed to update leave type", "error");
      }
    } catch (error) {
      showAlert("An error occurred while updating leave type", "error");
    } finally {
      setLoading(false);
    }
  };

  const getLeaveType = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getAccountLeaveType();
      if (result?.success) {
        setleaveData(result?.data);

        LoaderHelper.loaderStatus(false);
      } else {
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
      // console.error("Error occurred:", error);
    }
  };

  const getTravelAllowance = async () => {
    if (!selectedTaxYear || !taxCountry) {
      LoaderHelper.loaderStatus(false);
      return;
    }
    
    LoaderHelper.loaderStatus(true);
    try {
      const selectedFY = taxPeriod?.find((p) => p._id === selectedTaxYear);
      const startDate = selectedFY ? dayjs(selectedFY.start).format("YYYY-MM-DD") : "";
      const endDate = selectedFY ? dayjs(selectedFY.end).format("YYYY-MM-DD") : "";

      const result = await AuthService.getTravelsAllowances(startDate, endDate, taxCountry);
      if (result?.success) {
        const travelData = result?.data || [];
        setTravelAllowances(travelData);
        
        if (travelData.length === 0 && selectedTaxYear && startDate && endDate && taxCountry) {
          await handleCreateTravelAllowance(0, 100);
        }
        LoaderHelper.loaderStatus(false);
      } else {
        if (selectedTaxYear && startDate && endDate && taxCountry) {
          await handleCreateTravelAllowance(0, 100);
        }
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleCreateTravelAllowance = async (travelAllowanceRate, nonTaxableRate, start, end, country) => {
    const startDate = start || (taxPeriod?.find((p) => p._id === selectedTaxYear) ? dayjs(taxPeriod.find((p) => p._id === selectedTaxYear).start).format("YYYY-MM-DD") : "");
    const endDate = end || (taxPeriod?.find((p) => p._id === selectedTaxYear) ? dayjs(taxPeriod.find((p) => p._id === selectedTaxYear).end).format("YYYY-MM-DD") : "");
    const countryValue = country || taxCountry;

    if (!countryValue) {
      showAlert("Country is required", "error");
      return;
    }
    
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.createTravelAllowance(travelAllowanceRate, nonTaxableRate, startDate, endDate, countryValue);
      if (result?.success) {
        getTravelAllowance();
        showAlert(result?.message, "success")
      } else {
        showAlert(result?.message);
      }
    } catch (error) {
      showAlert(error);
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleEditTravelAllowance = async (id, travelAllowanceRate, nonTaxableRate, start, end, country) => {
    // Use passed parameters or fallback to extracting from state
    const startDate = start || (taxPeriod?.find((p) => p._id === selectedTaxYear) ? dayjs(taxPeriod.find((p) => p._id === selectedTaxYear).start).format("YYYY-MM-DD") : "");
    const endDate = end || (taxPeriod?.find((p) => p._id === selectedTaxYear) ? dayjs(taxPeriod.find((p) => p._id === selectedTaxYear).end).format("YYYY-MM-DD") : "");
    const countryValue = country || taxCountry;

    if (!countryValue) {
      showAlert("Country is required", "error");
      return;
    }
    
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.editTravelAllowance(id, travelAllowanceRate, nonTaxableRate, startDate, endDate, countryValue);
      if (result?.success) {
        getTravelAllowance();
        showAlert(result?.message, "success")
      } else {
        showAlert(result?.message);
      }
    } catch (error) {
      showAlert(error);
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };


  const handleTaxYearChange = (event) => {
    const selectedYearId = event.target.value;
    setSelectedTaxYear(selectedYearId);
  };

  return (
    <AllowancePage
      allowances={allowance}
      travelAllowanceData={travelAllowances}
      open={open}
      handleLeaveType={handleLeaveType}
      loading={loading}
      leaveData={leaveData}
      allowanceOpen={allowanceOpen}
      setAllowanceOpen={setAllowanceOpen}
      handleCreateAllowance={handleCreateAllowance}
      handleEditAllowance={handleEditAllowance}
      handleDeleteAllowance={handleDeleteAllowance}
      handleCreateTravelAllowance={handleCreateTravelAllowance}
      handleEditTravelAllowance={handleEditTravelAllowance}
      taxPeriod={taxPeriod}
      selectedTaxYear={selectedTaxYear}
      onTaxYearChange={handleTaxYearChange}
      taxCountry={taxCountry}
    />
  );
};

export default Allowance;
